import { memo, useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { copyObject } from 'common/copyObject';
import { useStore } from 'store/StoreContext';
import { useRegister } from 'hooks/UseNext4To5';
import { findSexTypeNameByKey } from 'consts/SexConst';
import { findCountryTypeNameByKey } from 'consts/CountryConst';
import { findPrefectureTypeNameByKey } from 'consts/PrefectureConst';
import { RegisterUserData } from 'store/register-user/RegisterUserData';
import { RoutePath } from 'consts/RoutePath';
import { CircularProgress, Backdrop } from '@material-ui/core';
import classes from './override.module.scss';
import { useSiteDisplayItems, SiteDisplayItemsExtend } from 'hooks/UseSiteDisplayItems';
import { Hr } from 'components/atoms/Hr';

type Props = {};
export type RegisterUserForm = RegisterUserData & {
	birthYear: string;
	birthMonth: string;
	birthDate: string;
};

export const UserRegister4Template = memo((_props: Props) => {
	const history = useHistory<SiteDisplayItemsExtend>();
	const [store, dispatch] = useStore();
	const registerUserData = copyObject(store.registerUserData);
	const birth = registerUserData.birth ? new Date(registerUserData.birth) : undefined;
	const [formData] = useState<RegisterUserForm>({
		birthYear: !!birth ? birth.getFullYear().toString() : '',
		birthMonth: !!birth ? (birth.getMonth() + 1).toString() : '',
		birthDate: !!birth ? birth.getDate().toString() : '',
		...registerUserData,
	});
	const { displayItems, isNone, getSiteDisplayItemLoading } = useSiteDisplayItems(store, history.location.state);
	const [processLoading, setProcessLoading] = useState(false);
	const { register } = useRegister(formData, store, dispatch, setProcessLoading);
	const back = () => {
		history.push(displayItems.isSkip3_2 ? RoutePath.USER_REGISTER_3_1 : RoutePath.USER_REGISTER_3_2, displayItems);
	};

	const scrollRef = useRef(null);
	useEffect(() => {
		if (!store.registerUserData.mailaddress_1) {
			console.error(`error: mailaddress not exists.`);
			history.push(RoutePath.ERROR);
		}
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenID新規登録｜登録内容確認';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (store.registerUserData.actionError !== '') {
			errorRef.current.scrollIntoView();
		}
	}, [store.registerUserData.actionError]);

	return (
		<main>
			<Backdrop className={classes.backdrop} open={getSiteDisplayItemLoading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<div className="section">
				<div className="section-in" ref={scrollRef}>
					<div className="steps">
						<img className="pc" src="images/img-steps-04.svg" alt="登録内容確認" />
						<img className="sp" src="images/img-steps-04_sp.svg" alt="" />
					</div>
					<div className="step-title sp">登録内容確認</div>
					<p>
						下記ご登録内容をご確認いただき、よろしければ「登録」ボタンを押してください。
						<br />
						入力内容を変更したい場合は「入力画面に戻る」ボタンから内容を変更してください。
					</p>

					{store.registerUserData.actionError !== '' ? (
						<div className="error-box" ref={errorRef}>
							<div className="error-box-in">
								<p>{store.registerUserData.actionError}</p>
							</div>
						</div>
					) : null}

					{isNone(displayItems.gid) ? null : (
						<div className="info-box">
							<div className="info-head">GakkenID</div>
							<div className={`info-txt ${classes.bigFontSize}`}>{formData.gid || '　'}</div>
						</div>
					)}
					{isNone(displayItems.pass) ? null : (
						<>
							<Hr />
							<div className="info-box">
								<div className="info-head">パスワード</div>
								<div className={`info-txt ${classes.bigFontSize}`}>**********</div>
							</div>
						</>
					)}
					{isNone(displayItems.lastname) ? null : (
						<>
							<Hr />
							<div className="info-box">
								<div className="info-head">お名前（全角）</div>
								<div className={`info-txt ${classes.bigFontSize}`}>{`${formData.lastname}${'　'}${
									formData.firstname
								}`}</div>
							</div>
						</>
					)}
					{isNone(displayItems.kana_lastname) ? null : (
						<>
							<Hr />
							<div className="info-box">
								<div className="info-head">お名前（カナ）</div>
								<div className={`info-txt ${classes.bigFontSize}`}>{`${formData.kana_lastname}${'　'}${
									formData.kana_firstname
								}`}</div>
							</div>
						</>
					)}
					{isNone(displayItems.birth) ? null : (
						<>
							<Hr />
							<div className="info-box">
								<div className="info-head">生年月日</div>
								<div className={`info-txt ${classes.bigFontSize}`}>{`${formData.birthYear}年${'　'}${
									formData.birthMonth
								}月${'　'}${formData.birthDate}日`}</div>
							</div>
						</>
					)}
					{isNone(displayItems.sex) ? null : (
						<>
							<Hr />
							<div className="info-box">
								<div className="info-head">性別</div>
								<div className={`info-txt ${classes.bigFontSize}`}>{findSexTypeNameByKey(formData.sex) || '　'}</div>
							</div>
						</>
					)}
					{isNone(displayItems.tel) ? null : (
						<>
							<Hr />
							<div className="info-box">
								<div className="info-head">電話番号</div>
								<div className={`info-txt ${classes.bigFontSize}`}>{formData.tel || '　'}</div>
							</div>
						</>
					)}
					{isNone(displayItems.zip) ? null : (
						<>
							<Hr />
							<div className="info-box">
								<div className="info-head">郵便番号</div>
								<div className={`info-txt ${classes.bigFontSize}`}>{formData.zip || '　'}</div>
							</div>
						</>
					)}
					{isNone(displayItems.pref_cd) ? null : (
						<>
							<Hr />
							<div className="info-box">
								<div className="info-head">都道府県</div>
								<div className={`info-txt ${classes.bigFontSize}`}>
									{findPrefectureTypeNameByKey(formData.pref_cd) || '　'}
								</div>
							</div>
						</>
					)}
					{isNone(displayItems.address_2) ? null : (
						<>
							<Hr />
							<div className="info-box">
								<div className="info-head">市区町村</div>
								<div className={`info-txt ${classes.bigFontSize}`}>{formData.address_2 || '　'}</div>
							</div>
						</>
					)}
					{isNone(displayItems.address_3) ? null : (
						<>
							<Hr />
							<div className="info-box">
								<div className="info-head">町名・番地</div>
								<div className={`info-txt ${classes.bigFontSize}`}>{formData.address_3 || '　'}</div>
							</div>
						</>
					)}
					{isNone(displayItems.address_4) ? null : (
						<>
							<Hr />
							<div className="info-box">
								<div className="info-head">マンション名</div>
								<div className={`info-txt ${classes.bigFontSize}`}>{formData.address_4 || '　'}</div>
							</div>
						</>
					)}
					{isNone(displayItems.nickname) ? null : (
						<>
							<Hr />
							<div className="info-box">
								<div className="info-head">ニックネーム</div>
								<div className={`info-txt ${classes.bigFontSize}`}>{formData.nickname || '　'}</div>
							</div>
						</>
					)}
					{isNone(displayItems.country) ? null : (
						<>
							<Hr />
							<div className="info-box">
								<div className="info-head">国</div>
								<div className={`info-txt ${classes.bigFontSize}`}>
									{findCountryTypeNameByKey(formData.country) || '　'}
								</div>
							</div>
						</>
					)}
					{isNone(displayItems.directmail_flg) ? null : (
						<>
							<Hr />
							<div className="info-box">
								<div className="info-head">同意内容</div>
								<div className={`info-txt ${classes.bigFontSize}`}>
									<ul>{formData.directmail_flg === 1 ? <li>GakkenIDのメルマガを受け取る</li> : <li>　</li>}</ul>
								</div>
							</div>
						</>
					)}

					<div className="btn-box">
						{processLoading ? (
							<CircularProgress />
						) : (
							<>
								<div className="btn wh">
									<button onClick={back}>入力画面に戻る</button>
								</div>
								<div className="btn gr">
									<button onClick={register}>登録</button>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</main>
	);
});
